import $ from 'jquery'

/**
 * Page scroll lock for overlays (lightboxes, popups)
 *
 * `overflow: hidden` is set on both the html and the body element: a theme
 * that gives html its own overflow keeps the body value from propagating to
 * the viewport, so locking the body alone leaves the page scrollable.
 * `overscroll-behavior` stops the page from taking over once a scrollable
 * overlay reaches its top or bottom, and the width the hidden scrollbar leaves
 * behind is padded back on so the layout stays still. Themes with smooth
 * scrolling (lenis) bypass overflow completely and are stopped through the
 * scroll:stop and scroll:start events instead.
 *
 * Locks are counted so an overlay opened on top of another one does not
 * release the page when only the topmost one closes. Call the functions on a
 * state change only, never on every click, to keep the count in step.
 */

let locks = 0
let restore = null

/**
 * Locks the page scroll
 *
 * @returns {void}
 *
 * @example
 * ```javascript
 * lockScroll()
 * ```
 */
export const lockScroll = () => {
	locks++

	if (locks > 1) {
		return
	}

	const html = document.documentElement
	const { body } = document
	const widthBefore = html.clientWidth

	restore = {
		htmlOverflow: html.style.overflow,
		htmlOverscrollBehavior: html.style.overscrollBehavior,
		bodyOverflow: body.style.overflow,
		bodyPaddingRight: body.style.paddingRight,
	}

	html.style.overflow = 'hidden'
	html.style.overscrollBehavior = 'none'
	body.style.overflow = 'hidden'

	// hiding the scrollbar widens the page, padding keeps the layout still
	const grown = html.clientWidth - widthBefore

	if (grown > 0) {
		const padding = parseFloat(getComputedStyle(body).paddingRight) || 0
		body.style.paddingRight = `${padding + grown}px`
	}

	$(document).trigger('scroll:stop')
}

/**
 * Releases the page scroll, once every lock is released
 *
 * @param {boolean} force releases every lock at once
 * @returns {void}
 *
 * @example
 * ```javascript
 * unlockScroll()
 * ```
 */
export const unlockScroll = (force = false) => {
	if (!locks) {
		return
	}

	locks = force ? 0 : locks - 1

	if (locks) {
		return
	}

	const html = document.documentElement
	const { body } = document

	html.style.overflow = restore.htmlOverflow
	html.style.overscrollBehavior = restore.htmlOverscrollBehavior
	body.style.overflow = restore.bodyOverflow
	body.style.paddingRight = restore.bodyPaddingRight
	restore = null

	$(document).trigger('scroll:start')
}
